import { useCallback } from 'react';

import { expValEquals } from '@confluence/feature-experiments';

import type { TitleAndButtonsProps } from './TitleAndButtons.tsx';

interface UseRefetchWrapperOnBackClickProps {
	titleAndButtons: TitleAndButtonsProps;
	isSpecificAccessTabOpen: boolean;
	refetchAccessRequest: () => void;
	refreshDialogState: () => Promise<void>;
}

/**
 * Hook that wraps the onBackClick callback to also trigger a refetch when closing
 * the specific access dialog. This ensures fresh data when users reopen the dialog.
 *
 * The wrapper is only applied when ALL conditions are met:
 * 1. Specific access dialog is open
 * 2. Original onBackClick exists
 * 3. persistence_specific_access_gate experiment is enabled
 */
export const useRefetchWrapperOnBackClick = ({
	titleAndButtons,
	isSpecificAccessTabOpen,
	refetchAccessRequest,
	refreshDialogState,
}: UseRefetchWrapperOnBackClickProps): TitleAndButtonsProps => {
	const wrappedOnBackClick = useCallback(async () => {
		refetchAccessRequest();
		titleAndButtons.onBackClick?.();
		await refreshDialogState();
	}, [titleAndButtons, refetchAccessRequest, refreshDialogState]);

	const shouldApplyWrapper =
		isSpecificAccessTabOpen &&
		titleAndButtons.onBackClick &&
		expValEquals('persistence_specific_access_gate', 'cohort', 'experiment');

	return shouldApplyWrapper
		? {
				...titleAndButtons,
				onBackClick: wrappedOnBackClick,
			}
		: titleAndButtons;
};
